import { useContext, useEffect, useState } from "react";
import { CartContext } from "../../../context/cart.context";
import { UserContext } from "../../../context/user.context";
import axiosApi from "../../../api/axiosInstance";
import { getDeliveryLocations } from "../../../utils/zulalWebAPI";
import { getProductsByPriceListCode } from "../../../utils/ProductsApi";
import { toast } from "react-toastify";

export default function useCheckoutLogic() {
  const {
    cartItems,
    shippingCharges,
    cartGTotal,
    cartCount,
    actualTotal,
    cartDiscount,
    setDeliveryOption: setContextDeliveryOption,
    setMosquePrices: setContextMosquePrices,
    deliveryOption: contextDeliveryOption,
    mosquePrices: contextMosquePrices,
    bottleDepositAmount,
    setBottleDepositAmount,
    cartGrandTotal,
  } = useContext(CartContext);

  const { currentUser } = useContext(UserContext);

  const [deliveryOption, setDeliveryOption] = useState(
    contextDeliveryOption || "delivery"
  );

  const [mosquePrices, setMosquePrices] = useState(contextMosquePrices || {});
  const [loading, setLoading] = useState(false);
  const [selectedMosque, setSelectedMosque] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [fiveGallonNotice, setFiveGallonNotice] = useState(false);
  const [donateWater, setDonateWater] = useState(false);

  const [showMosqueConfirmation, setShowMosqueConfirmation] = useState(false);
  const [showBottleDepositConfirmation, setShowBottleDepositConfirmation] =
    useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingOrderSummary, setPendingOrderSummary] = useState(null);

  // Partial delivery (5-gallon bulk orders)
  const [showPartialDeliveryDialog, setShowPartialDeliveryDialog] =
    useState(false);
  const [partialDeliverySchedule, setPartialDeliverySchedule] = useState(null);

  const [mosqueConfirmed, setMosqueConfirmed] = useState(false);
  const [bottleDepositConfirmed, setBottleDepositConfirmed] = useState(false);

  const [returnBottlesCount, setReturnBottlesCount] = useState(0);

  const [bottleDepositAcknowledged, setBottleDepositAcknowledged] =
    useState(false);

  // Sonic's recorded empty-bottle count for this customer (deposit +
  // non-deposit containers), fetched from getcontainerlimit. Used to warn
  // AND to clamp the deposit calculation -- see getEffectiveReturnBottlesCount
  // below. This is fully dynamic per customer: no hardcoded thresholds
  // anywhere -- new customers with 0 on record get clamped to 0, customers
  // with any other recorded count get clamped to exactly that value.
  const [containerLimit, setContainerLimit] = useState({
    deposit: 0,
    nonDeposit: 0,
  });

  // Read directly from the per-environment .env file (.env.qa, .env.production,
  // etc.) instead of NODE_ENV -- react-scripts build always forces NODE_ENV to
  // "production" regardless of target server, so it can't distinguish QA from
  // real production. Falls back to QA/dev codes if the var isn't set.

  const DEPOSIT_UNIT_PRICE = 10;
  const RETURN_ITEM_CODE = process.env.REACT_APP_RETURN_ITEM_CODE || "9004710";

  const GALLON_ITEM_CODE = process.env.REACT_APP_GALLON_ITEM_CODE || "9000464";
  const gallonItemCodes = [GALLON_ITEM_CODE];

  // Partial delivery trigger threshold -- button appears when qty exceeds this.
  // Configurable via env var so business can change it without a redeploy;
  // backend has its own matching default (see partialDeliveryPersist.js /
  // salesOrderService.js) -- this only controls the UI trigger, the backend
  // never trusts a client-sent threshold.
  const PARTIAL_DELIVERY_MIN_QTY =
    parseInt(process.env.REACT_APP_PARTIAL_DELIVERY_MIN_QTY, 10) || 6;

  const has5GallonBottles = () => {
    return cartItems.some((item) => gallonItemCodes.includes(String(item.id)));
  };

  const get5GallonBottleCount = () => {
    return cartItems
      .filter((item) => gallonItemCodes.includes(String(item.id)))
      .reduce((total, item) => total + (item.quantity || 1), 0);
  };

  // The 5-gallon SKU differs between QA (9004278) and PROD (9000464), but
  // both environments only ever have ONE of these in cartItems at a time --
  // so no blending across SKUs is needed, just find whichever is present.
  const getFiveGallonLineItem = () => {
    return cartItems.find((item) => gallonItemCodes.includes(String(item.id)));
  };

  // item.price on the cart line is already the correctly discounted
  // per-bottle price (calculated in cart.context.jsx via resolvePrice/
  // getDiscountRate) -- read it directly, no re-derivation needed here.
  const getFiveGallonUnitPrice = () => {
    const item = getFiveGallonLineItem();
    return item ? parseFloat(item.price) : 0;
  };

  const getFiveGallonItemId = () => {
    const item = getFiveGallonLineItem();
    return item ? item.id : null;
  };

  // Security deposit is a ONE-TIME charge scoped to delivery #1's
  // quantity only. Deliveries 2+ are treated as straight empty-for-full
  // exchanges (driver swaps bottles, deducts invoice amount from wallet) --
  // no additional deposit is collected on those.
  const getDepositEligibleQty = () => {
    if (partialDeliverySchedule?.deliveries?.length) {
      return partialDeliverySchedule.deliveries[0].quantity;
    }
    return get5GallonBottleCount();
  };

  // Total empty 5-gallon bottles Sonic has on record for this customer
  // (deposit + non-deposit containers combined). Returns null while still
  // loading / if the fetch failed, so callers can distinguish "unknown"
  // from "confirmed zero" and avoid showing a false warning or clamping
  // against a value that hasn't loaded yet.
  const getRecordedEmptyBottleCount = () => {
    if (!containerLimit) return null;
    return (containerLimit.deposit || 0) + (containerLimit.nonDeposit || 0);
  };

  // The customer's declared returnBottlesCount is clamped to the recorded
  // limit for CALCULATION purposes -- deposit owed always reflects what
  // Sonic actually has on file for THIS customer, never their unverified
  // claim. Fully dynamic, no fixed number anywhere: a new customer with 0
  // recorded gets clamped to 0 (pays full deposit); a customer with any
  // other recorded count gets clamped to exactly that count.
  // The raw returnBottlesCount input itself is left untouched so the
  // warning/UI can still show "you entered X, records show Y" for
  // transparency, and the Confirm button stays disabled until corrected.
  const getEffectiveReturnBottlesCount = () => {
    const recorded = getRecordedEmptyBottleCount();
    if (recorded === null) return returnBottlesCount; // still loading/unknown, trust input for now
    return Math.min(returnBottlesCount, recorded);
  };

  const getBottlesNeedingDeposit = () => {
    const totalBottles = getDepositEligibleQty();
    const needDeposit = Math.max(
      0,
      totalBottles - getEffectiveReturnBottlesCount()
    );
    return needDeposit;
  };

  const getTotalDepositAmount = () => {
    return getBottlesNeedingDeposit() * DEPOSIT_UNIT_PRICE;
  };

  // Informational + blocking warning when the customer's declared
  // returnBottlesCount exceeds what Sonic has on record. The Confirm
  // button in CheckOut.jsx is disabled while this returns non-null, so the
  // customer must correct their entry (or contact customer care) before
  // proceeding -- they cannot self-declare more empty bottles than Sonic
  // has on file to reduce their deposit.
  const getContainerLimitWarning = () => {
    const recorded = getRecordedEmptyBottleCount();
    if (recorded === null) return null; // still loading or fetch failed
    if (returnBottlesCount > 0 && returnBottlesCount > recorded) {
      if (recorded === 0) {
        return "Our records show you don't have empty gallon bottles. If this is not correct and you do have empty gallon bottles, please consult with customer care Team.";
      }
      return `Our records show you have ${recorded} empty gallon bottle${
        recorded === 1 ? "" : "s"
      } on file, which is fewer than the ${returnBottlesCount} you've entered. If this is not correct, please consult with customer care Team.`;
    }
    return null;
  };

  // Kept for the pre-submit confirm() dialog display only -- this is a
  // client-side estimate for UX. The server independently computes the
  // authoritative order total and never trusts this value for the charge.
  // NOTE: always uses the FULL cart (all items, full quantities) -- the
  // customer pays the full order amount upfront regardless of partial
  // delivery scheduling; that payment funds their Wallet, and Sonic orders
  // for each scheduled delivery draw down from it separately (backend, TBD).
  const calculateOrderSummary = (items, depositQty, depositUnitPrice) => {
    let subtotal = 0;
    let taxTotal = 0;

    items.forEach((item) => {
      const qty = parseInt(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const lineTotal = qty * price;
      const lineTax = parseFloat((lineTotal * 0.05).toFixed(2));

      subtotal += lineTotal;
      taxTotal += lineTax;
    });

    const depositTotal = depositQty * depositUnitPrice;

    const grandTotal = parseFloat(
      (subtotal + taxTotal + depositTotal).toFixed(2)
    );

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxTotal: parseFloat(taxTotal.toFixed(2)),
      depositTotal,
      grandTotal,
    };
  };

  useEffect(() => {
    const depositAmount = getTotalDepositAmount();
    setBottleDepositAmount(depositAmount);
  }, [returnBottlesCount, cartItems, setBottleDepositAmount]);

  useEffect(() => {
    setReturnBottlesCount(0);
    setBottleDepositAmount(0);
    setBottleDepositAcknowledged(false);
  }, [deliveryOption, setBottleDepositAmount]);

  // Clear an in-progress/confirmed partial delivery schedule if it's no
  // longer applicable (switched to mosque, or quantity dropped to/below
  // threshold) -- OR if it's gone stale because the cart quantity changed
  // to something the schedule's deliveries no longer sum to. A stale
  // schedule would otherwise be silently rejected server-side at
  // confirmation time (quantities won't match), so the customer needs to
  // redo it here instead of finding out after paying.
  useEffect(() => {
    const qty = get5GallonBottleCount();

    if (deliveryOption !== "delivery" || qty <= PARTIAL_DELIVERY_MIN_QTY) {
      if (partialDeliverySchedule) {
        setPartialDeliverySchedule(null);
        setShowPartialDeliveryDialog(false);
      }
      return;
    }

    if (partialDeliverySchedule) {
      const scheduleTotal = partialDeliverySchedule.deliveries.reduce(
        (sum, d) => sum + (Number(d.quantity) || 0),
        0
      );
      if (scheduleTotal !== qty) {
        setPartialDeliverySchedule(null);
        setShowPartialDeliveryDialog(false);
        toast.info(
          "Your bottle quantity changed — please set up your delivery schedule again."
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryOption, cartItems]);

  // Any change to the delivery schedule (set, changed, or cleared)
  // invalidates any previous empty-bottle confirmation -- delivery #1's
  // quantity (which the deposit is scoped to) may be different now, so we
  // require the customer to reconfirm rather than silently carrying
  // forward a stale count/acknowledgement.
  useEffect(() => {
    setReturnBottlesCount(0);
    setBottleDepositAcknowledged(false);
  }, [partialDeliverySchedule]);

  useEffect(() => {
    setContextDeliveryOption(deliveryOption);
  }, [deliveryOption, setContextDeliveryOption]);

  useEffect(() => {
    setMosqueConfirmed(false);
    setBottleDepositConfirmed(false);
  }, [deliveryOption, selectedMosque, cartItems]);

  useEffect(() => {
    async function fetchAddresses() {
      try {
        const customerCode = currentUser.customerCode;
        const response = await getDeliveryLocations(customerCode);
        setLocations(response.data || []);
      } catch (error) {
        console.error("Error fetching addresses:", error);
      }
    }
    if (currentUser?.customerCode) {
      fetchAddresses();
    }
  }, [currentUser?.customerCode]);

  // Fetch the customer's recorded empty-bottle container limit. Fetched
  // early (on customer load) so it's already available by the time the
  // customer opens the bottle deposit confirmation dialog. Failure is
  // non-blocking: containerLimit just stays null, and both
  // getContainerLimitWarning() and getEffectiveReturnBottlesCount() fall
  // back to trusting the raw input in that case (no false warning, no
  // incorrect clamp against an unknown value).
  useEffect(() => {
    async function fetchContainerLimit() {
      try {
        console.log("DEBUG calling getcontainerlimit...");
        const response = await axiosApi.get(
          "/api/v2/customer/getcontainerlimit"
        );
        console.log("DEBUG containerLimit response:", response.data);
        if (response.data?.success) {
          setContainerLimit(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching container limit:", error);
      }
    }
    if (currentUser?.customerCode) {
      fetchContainerLimit();
    }
  }, [currentUser?.customerCode]);
  useEffect(() => {
    if (locations && locations.length > 0) {
      const defaultLoc = locations.find((loc) => loc.isDefault);
      setSelectedLocation(defaultLoc || locations[0]);
      setDeliveryOption("delivery");
    }
  }, [locations]);

  useEffect(() => {
    let cancelled = false;

    const fetchMosquePrices = async () => {
      try {
        if (!(deliveryOption === "mosque" && selectedMosque?.MosqueID)) {
          setMosquePrices({});
          setContextMosquePrices({});
          setDonateWater(deliveryOption === "mosque");
          return;
        }

        setLoading(true);
        setDonateWater(true);

        const listCode = process.env.NODE_ENV === "production" ? "MSP" : "MSQ";

        const response = await getProductsByPriceListCode({
          listCode: listCode,
        });

        const priceMap = {};
        (response?.itemDetails || []).forEach((item) => {
          const code = String(item.itemCode || "").trim();
          const price = Number(item.price);
          if (code && !Number.isNaN(price)) {
            priceMap[code] = price;
          }
        });

        if (cancelled) return;

        setMosquePrices(priceMap);
        setContextMosquePrices(priceMap);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching mosque prices:", error);
          toast.error("Failed to load mosque prices");
          setMosquePrices({});
          setContextMosquePrices({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMosquePrices();

    return () => {
      cancelled = true;
    };
  }, [deliveryOption, selectedMosque?.MosqueID, setContextMosquePrices]);

  useEffect(() => {
    setFiveGallonNotice(
      cartItems.some((item) => gallonItemCodes.includes(String(item.id)))
    );
  }, [cartItems]);

  function getDisplayPrice(item) {
    if (deliveryOption === "mosque" && mosquePrices[item.id]) {
      return {
        ...item,
        price: mosquePrices[item.id],
        actualprice: item.actualprice,
      };
    }
    return item;
  }

  function getRawPrice(item) {
    if (deliveryOption === "mosque" && mosquePrices[item.id]) {
      return parseFloat(mosquePrices[item.id]);
    }
    return parseFloat(item.price);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser?.customerCode) {
      toast.error("Please login to continue");
      return;
    }

    if (!currentUser?.email) {
      toast.error("Please update your email address in profile");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    if (deliveryOption === "mosque") {
      if (!selectedMosque?.MosqueID || !selectedMosque?.MosqueName) {
        toast.error("Please select a mosque");
        return;
      }
    }

    if (deliveryOption === "delivery") {
      if (!selectedLocation?.locationId) {
        toast.error("Please select a delivery location");
        return;
      }
    }

    if (deliveryOption === "mosque" && !mosqueConfirmed) {
      setShowMosqueConfirmation(true);
      return;
    }

    if (has5GallonBottles() && !bottleDepositAcknowledged) {
      toast.warning("Please review and confirm the bottle deposit");
      setShowBottleDepositConfirmation(true);
      return;
    }

    // All validation passed -- build the display summary and hand off
    // to the custom Payment Confirmation dialog instead of window.confirm/alert.
    // Uses the FULL cart total (customer pays full amount, which funds their
    // Wallet; partial deliveries are drawn from it separately by the backend).
    const depositQty = getBottlesNeedingDeposit();
    const orderSummary = calculateOrderSummary(
      cartItems,
      depositQty,
      DEPOSIT_UNIT_PRICE
    );

    setPendingOrderSummary(orderSummary);
    setShowPaymentConfirmation(true);
  };

  const cancelPaymentConfirmation = () => {
    setShowPaymentConfirmation(false);
    setPendingOrderSummary(null);
  };

  const confirmAndProceedToPayment = async () => {
    setShowPaymentConfirmation(false);

    try {
      setLoading(true);

      const depositQty = getBottlesNeedingDeposit();
      const total5GQty = get5GallonBottleCount();

      // payload trimmed to match the backend contract -- server resolves
      // email/name/delivery-location/pricing itself. Client only sends product
      // IDs + quantities and reference counts; nothing here is trusted for the
      // actual charge.
      const orderData = {
        cartItems: cartItems.map((item) => ({
          id: item.id,
          uom: item.uom,
          quantity: item.quantity,
        })),
        deliveryOption: deliveryOption,
        bottleDepositAmount: pendingOrderSummary?.depositTotal ?? 0, // reference only -- server recomputes
        returnBottlesCount: returnBottlesCount,
        depositBottleCount: depositQty, // reference only -- server recomputes authoritatively
        total5GallonQty: total5GQty, // reference only -- server recomputes authoritatively
        orderSource: "web",
        donateWater: deliveryOption === "mosque", // reference only -- server derives this from deliveryOption
      };

      if (deliveryOption === "mosque") {
        orderData.selectedMosque = {
          MosqueID: selectedMosque.MosqueID,
          MosqueName: selectedMosque.MosqueName,
        };
      } else {
        orderData.selectedLocationID = selectedLocation.locationId;

        // Reference only -- full payment funds the customer's Wallet.
        // Backend persists the exact dates/quantities the customer chose
        // (see persistCustomerChosenSchedule) -- pricing fields in this
        // object are never trusted, only date + quantity per delivery.
        if (partialDeliverySchedule) {
          orderData.partialDeliverySchedule = partialDeliverySchedule;
        }
      }

      console.log("Submitting order data:", orderData);

      toast.info("Creating your order...", { autoClose: 2000 });

      const response = await axiosApi.post(
        `/api/v2/customer/orders/salesorder`,
        orderData
      );

      if (response.data.success && response.data.payByLinkUrl) {
        toast.success("Redirecting to secure payment gateway...", {
          autoClose: 2000,
        });

        setTimeout(() => {
          window.location.href = response.data.payByLinkUrl;
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating order:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create order. Please try again.";

      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleMosqueConfirm = () => {
    setMosqueConfirmed(true);
    setShowMosqueConfirmation(false);

    if (has5GallonBottles()) {
      setShowBottleDepositConfirmation(true);
    } else {
      const form = document.getElementById("checkout-form");
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleBottleDepositConfirm = () => {
    setBottleDepositConfirmed(true);
    setShowBottleDepositConfirmation(false);

    const form = document.getElementById("checkout-form");
    if (form) {
      form.requestSubmit();
    }
  };

  return {
    deliveryOption,
    setDeliveryOption,
    locations,
    selectedLocation,
    setSelectedLocation,
    selectedMosque,
    setSelectedMosque,
    donateWater,
    loading,
    cartItems,
    cartCount,
    cartGTotal,
    handleSubmit,
    fiveGallonNotice,
    shippingCharges,
    actualTotal,
    cartDiscount,
    mosquePrices,
    getDisplayPrice,
    getRawPrice,

    showMosqueConfirmation,
    setShowMosqueConfirmation,
    showBottleDepositConfirmation,
    setShowBottleDepositConfirmation,
    showPaymentConfirmation,
    pendingOrderSummary,
    cancelPaymentConfirmation,
    confirmAndProceedToPayment,

    // Partial delivery
    showPartialDeliveryDialog,
    setShowPartialDeliveryDialog,
    partialDeliverySchedule,
    setPartialDeliverySchedule,
    getFiveGallonItemId,
    getFiveGallonUnitPrice,
    getFiveGallonLineItem,
    getDepositEligibleQty,
    PARTIAL_DELIVERY_MIN_QTY,

    mosqueConfirmed,
    bottleDepositConfirmed,
    has5GallonBottles,
    get5GallonBottleCount,
    handleMosqueConfirm,
    handleBottleDepositConfirm,

    returnBottlesCount,
    setReturnBottlesCount,
    returnItemCode: RETURN_ITEM_CODE,

    getBottlesNeedingDeposit,
    getTotalDepositAmount,
    cartGrandTotal,
    bottleDepositAmount,
    bottleDepositAcknowledged,
    setBottleDepositAcknowledged,

    calculateOrderSummary,

    // Container limit / empty bottle records validation
    containerLimit,
    getRecordedEmptyBottleCount,
    getContainerLimitWarning,
    getEffectiveReturnBottlesCount,
  };
}
